/**
 * ErrorState — presentational component shown when admission data fails to load.
 *
 * Displays the load-error message and a retry control. Purely presentational:
 * it renders the message it is given and invokes `onRetry` when the retry
 * control is activated (Requirements 2.4, 2.5).
 */

import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';

export interface ErrorStateProps {
  /** The load-error message to display. */
  message?: string;
  /** Invoked when the user activates the retry control. */
  onRetry?: () => void;
  /** Optional label for the retry control. */
  retryLabel?: string;
}

const DEFAULT_MESSAGE = 'Admission data could not be loaded.';

const ErrorState: React.FC<ErrorStateProps> = ({
  message = DEFAULT_MESSAGE,
  onRetry,
  retryLabel = 'Retry',
}) => (
  <Box
    role="alert"
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      gap: 2,
      py: 6,
      px: 3,
    }}
  >
    <ErrorOutlineIcon sx={{ fontSize: 56, color: 'error.main' }} />
    <Typography variant="h6" color="error.main">
      {message}
    </Typography>
    {onRetry && (
      <Button
        variant="contained"
        color="primary"
        startIcon={<RefreshIcon />}
        onClick={onRetry}
      >
        {retryLabel}
      </Button>
    )}
  </Box>
);

export default ErrorState;
